/**
 * Cart Recommendations Handler
 * Handles product recommendations in the mini cart with quick add functionality
 * Imperial Candles Theme Enhancement
 */

(function() {
  'use strict';

  /**
   * Initialize cart recommendations when cart loads
   */
  function initCartRecommendations() {
    // Initialize quick add buttons
    const quickAddButtons = document.querySelectorAll('[data-quick-add-to-cart]');
    quickAddButtons.forEach(button => {
      button.addEventListener('click', handleQuickAdd);
    });

    // Initialize variant selectors
    const variantSelectors = document.querySelectorAll('[data-product-variants]');
    variantSelectors.forEach(select => {
      select.addEventListener('change', handleVariantChange);
    });

    // Check if currency conversion is needed
    if (typeof window.sharedFunctions !== 'undefined' &&
        typeof window.sharedFunctions.checkNeedToConvertCurrency === 'function' &&
        window.sharedFunctions.checkNeedToConvertCurrency()) {
      convertRecommendationPrices();
    }
  }

  /**
   * Handle variant selection change
   */
  function handleVariantChange(event) {
    const select = event.target;
    const variantId = select.value;
    const productCard = select.closest('.cart-rec-card');
    const addButton = productCard.querySelector('[data-quick-add-to-cart]');

    // Update button's variant ID
    if (addButton) {
      addButton.dataset.variantId = variantId;

      // Check if variant is available
      const selectedOption = select.options[select.selectedIndex];
      const isAvailable = selectedOption.dataset.available === 'true';

      addButton.disabled = !isAvailable;
      addButton.setAttribute('aria-disabled', !isAvailable);
    }
  }

  /**
   * Handle quick add to cart
   */
  async function handleQuickAdd(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;

    if (!variantId || button.disabled) {
      return;
    }

    // Set loading state
    button.classList.add('loading');
    button.disabled = true;

    try {
      // Add to cart via Shopify Cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const item = await response.json();

      // Get updated cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Show success toast
      showToast('Added to cart!', 'success');

      // Update cart display
      updateCartDisplay(cart);

      // Track analytics if available
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({
          'event': 'addToCart',
          'ecommerce': {
            'add': {
              'products': [{
                'id': item.product_id,
                'variant_id': item.variant_id,
                'name': item.product_title,
                'price': item.price / 100,
                'quantity': 1
              }]
            }
          }
        });
      }

    } catch (error) {
      console.error('Add to cart error:', error);
      showToast('Failed to add to cart. Please try again.', 'error');
    } finally {
      // Remove loading state
      button.classList.remove('loading');
      button.disabled = false;
    }
  }

  /**
   * Update cart display after adding item
   */
  function updateCartDisplay(cart) {
    // Update cart count
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(el => {
      el.textContent = cart.item_count;
    });

    // Update sidebar cart if halo.updateSidebarCart exists
    if (typeof halo !== 'undefined' && typeof halo.updateSidebarCart === 'function') {
      halo.updateSidebarCart(cart);
    } else {
      // Fallback: Reload cart sidebar content
      reloadCartSidebar();
    }
  }

  /**
   * Reload cart sidebar content via AJAX
   */
  async function reloadCartSidebar() {
    try {
      const response = await fetch(window.routes.root + '/cart?view=ajax_side_cart', {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const html = await response.text();

      // Update cart content
      const cartDropdown = document.querySelector('.halo-sidebar-wrapper');
      if (cartDropdown) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContent = doc.querySelector('.halo-sidebar-wrapper');

        if (newContent) {
          cartDropdown.innerHTML = newContent.innerHTML;

          // Reinitialize recommendations
          initCartRecommendations();

          // Trigger any other necessary re-initializations
          if (typeof halo !== 'undefined') {
            if (typeof halo.dispatchChangeForShippingMessage === 'function') {
              halo.dispatchChangeForShippingMessage();
            }
            if (typeof halo.productCollectionCartSlider === 'function') {
              halo.productCollectionCartSlider();
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to reload cart:', error);
    }
  }

  /**
   * Convert recommendation prices if currency conversion is enabled
   */
  function convertRecommendationPrices() {
    if (typeof Currency === 'undefined' ||
        typeof Currency.convertAll !== 'function') {
      return;
    }

    const activeCurrency = document.querySelector('#currencies .active');
    if (!activeCurrency) {
      return;
    }

    const targetCurrency = activeCurrency.dataset.currency;

    if (window.shop_currency && targetCurrency) {
      Currency.convertAll(
        window.shop_currency,
        targetCurrency,
        'span.money',
        'money_format'
      );
    }
  }

  /**
   * Show toast notification
   */
  function showToast(message, type = 'success') {
    // Remove any existing toasts
    const existingToasts = document.querySelectorAll('.cart-toast');
    existingToasts.forEach(toast => toast.remove());

    // Create toast element
    const toast = document.createElement('div');
    toast.className = `cart-toast ${type}`;

    const icon = type === 'success'
      ? '<svg class="cart-toast__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>'
      : '<svg class="cart-toast__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>';

    toast.innerHTML = `
      ${icon}
      <span class="cart-toast__message">${message}</span>
    `;

    // Add to page
    document.body.appendChild(toast);

    // Auto-hide after 3 seconds
    setTimeout(() => {
      toast.classList.add('hiding');
      setTimeout(() => {
        toast.remove();
      }, 300);
    }, 3000);
  }

  /**
   * Initialize on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartRecommendations);
  } else {
    initCartRecommendations();
  }

  /**
   * Re-initialize when cart updates (listen for custom events)
   */
  document.addEventListener('cart:updated', initCartRecommendations);

  /**
   * Expose public API
   */
  window.CartRecommendations = {
    init: initCartRecommendations,
    showToast: showToast,
    convertPrices: convertRecommendationPrices
  };

})();
